import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useIsAdminUser } from "@/providers/auth/hooks";
import { RobotoStyledTab, RobotoStyledTabs } from "@/shared/components";

import { MetricDefinitionsTable } from "../MetricDefinitionsTable/MetricDefinitionsTable";
import { MetricsDashboard } from "../MetricsDashboard/MetricsDashboard";

import styles from "./MetricsContent.module.css";

const TAB_PARAM = "tab";

type MetricsTab = "dashboard" | "definitions";

export interface MetricsContentProps {
  orgId: string | undefined;
}

/**
 * Body of the top-level Metrics route. Admin users — org admins/owners or
 * Roboto system users (see `useIsAdminUser`) — get a tab bar with "Dashboard"
 * (the charts/tiles view) and "Metric Definitions" (the org-wide management
 * table). Non-admins see only the dashboard with no tab bar; the management
 * table is unreachable for them, and `MetricsDashboard` never renders it itself.
 *
 * The active tab lives in the `?tab=` search param so it survives reload and is
 * shareable; an absent or unknown value means the Dashboard tab. The param is
 * inert for non-admins, who can never reach the definitions view.
 */
export function MetricsContent({ orgId }: MetricsContentProps) {
  const isAdmin = useIsAdminUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: MetricsTab =
    searchParams.get(TAB_PARAM) === "definitions" ? "definitions" : "dashboard";

  const handleTabChange = (_event: React.SyntheticEvent, next: MetricsTab) => {
    // Preserve the dashboard's own range/period params; only flip `tab`. Use
    // `replace` so tab switches don't pile up in history (matches the
    // dashboard controls and DeviceTabs).
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "dashboard") {
          nextParams.delete(TAB_PARAM);
        } else {
          nextParams.set(TAB_PARAM, next);
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  if (!isAdmin) {
    return <MetricsDashboard orgId={orgId} />;
  }

  return (
    <div
      className={
        activeTab === "definitions"
          ? styles.definitionsContainer
          : styles.container
      }
    >
      <RobotoStyledTabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="metrics sections"
        className={styles.tabs}
      >
        <RobotoStyledTab value="dashboard" label="Dashboard" />
        <RobotoStyledTab value="definitions" label="Metric Definitions" />
      </RobotoStyledTabs>

      {activeTab === "dashboard" ? (
        <MetricsDashboard orgId={orgId} />
      ) : (
        <MetricDefinitionsTable orgId={orgId} />
      )}
    </div>
  );
}
